import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "../css/others/Table.css";
import Pagination from "../Reusablecomponents/Pagination";
import ExportButton from "../Reusablecomponents/ExportButton";
import "../css/others/Popup.css";
import ToastPopup from "../popups/ToastPopup";
import { apiCall } from "../main/ApiCall";
import { buildUserPayload } from "../main/buildUserPayload";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { IFunction, FunctionEnum, FunctionCampaignStatusEnum, createToastMessage, ToastMessage } from '../interFaces/interfaces';
import EncryptionUtils from "../main/encryptionUtils";
const FunctionCampaignDetails: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [functions, setFunctions] = useState<IFunction[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [selectedFunctions, setSelectedFunctions] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const campaignName = location.state?.campaignName;
  const campaignStatus = location.state?.campaignStatus;
  const [functionEnums, setFunctionEnums] = useState<FunctionEnum[]>([]);
  const [campaignStatusEnum, setCampaignStatusEnum] = useState<FunctionCampaignStatusEnum[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>(null);
  const [fields, setFields] = useState<any>({});
  useEffect(() => {
    const permissions = EncryptionUtils.get<any>("permissions", null);
    if (permissions.tabs["functionCampaigns"]) {
      setFields(permissions.tabs["functionCampaigns"].fields);
    } else {
      console.log("No permissions found for 'functionCampaigns' tab");
    }
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const payload = buildUserPayload();
        const requestBody = [{ field: "campaignId", operator: "=", value: campaignId }, { field: "isAvailable", operator: "=", value: false },
        ...(payload ? [payload] : [])];
        const func = await apiCall("post", `/api/campaign-functions/search?page=${currentPage - 1}&size=${itemsPerPage}`, requestBody);
        setFunctions(func?.content || []);
        setTotalPages(func?.totalPages || 0);
        setTotalElements(func?.totalElements || 0);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [campaignId, currentPage, itemsPerPage]);
  useEffect(() => {
    const fetchFunctionEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/FunctionEnum");
        setFunctionEnums(response || []);
      } catch (error) {
        console.error("Error fetching function enums:", error);
      }
    };
    fetchFunctionEnums();
  }, []);
  useEffect(() => {
    const fetchCampaignStatusEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/CampaignStatusEnum");
        setCampaignStatusEnum(response || []);
      } catch (error) {
        console.error("Error fetching campaign status enums:", error);
      }
    };
    fetchCampaignStatusEnums();
  }, []);
  const getFunctionDescription = (value: string) => {
    const enumItem = functionEnums.find(item => item.value === value);
    return enumItem ? enumItem.description : value || "N/A";
  };
  const getCampaignStatusDescription = (value: string) => {
    const enumItem = campaignStatusEnum.find(item => item.value === value);
    return enumItem ? enumItem.description : value || "N/A";
  };
  const fetchAllDataForExport = async (): Promise<IFunction[]> => {
    try {
      const payload = buildUserPayload();
      const requestBody = [{ field: "campaignId", operator: "=", value: campaignId }, { field: "isAvailable", operator: "=", value: false },
      ...(payload ? [payload] : [])];
      const response = await apiCall("post", `/api/campaign-functions/search?page=0&size=${totalElements}`, requestBody);
      const transformedData = (response?.content || []).map((func: { functionName: string; auditStatus: string; }) => ({ ...func, functionName: getFunctionDescription(func.functionName) || "N/A", auditStatus: getCampaignStatusDescription(func.auditStatus || "N/A"), }));
      return transformedData;
    } catch (error) {
      console.error("Error fetching all functions for export:", error);
      setMessage(createToastMessage("Failed to export functions. Please try again later.", "error", 3000));
      return [];
    }
  };
  useEffect(() => {
    setSelectAll(functions.length > 0 && functions.every(func => selectedFunctions.has(func.functionId)));
  }, [functions, selectedFunctions]);
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getFullYear()}`;
  };
  const handleCheckboxChange = (functionId: string) => {
    const updatedSelection = new Set(selectedFunctions);
    updatedSelection.has(functionId) ? updatedSelection.delete(functionId) : updatedSelection.add(functionId);
    setSelectedFunctions(updatedSelection);
  };
  const handleSelectAllChange = () => {
    setSelectedFunctions(selectAll ? new Set() : new Set(functions.map(f => f.functionId)));
  };
  const handleRemoveSelectedFunctions = async () => {
    if (!campaignId || selectedFunctions.size === 0) return;
    try {
      const body = { campaignId, functionIds: Array.from(selectedFunctions) };
      const response = await apiCall("post", "/api/campaign-functions/unassign", body);
      if (response) {
        setFunctions(functions.filter(func => !selectedFunctions.has(func.functionId)));
        setTotalElements(prev => prev - selectedFunctions.size);
        setMessage(createToastMessage("Functions removed successfully!", "success", 3000));
        setSelectedFunctions(new Set());
      } else {
        throw new Error("No response from API");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to remove functions.";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
  };
  const requestSort = (key: string) => {
    const direction = (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') ? 'descending' : 'ascending';
    setSortConfig({ key, direction });
    const sortedFunctions = [...functions].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
    setFunctions(sortedFunctions);
  };
  const columns = [{ key: "functionName", label: "Function Campaign Name", sortable: true }, { key: "functionHeadName", label: "Function Head Name" }, { key: "functionHeadEmail", label: "Function Head Email" }, { key: "functionSpocName", label: "Function Spoc Name" }, { key: "functionSpocEmail", label: "Function Spoc Email" }, { key: "auditStatus", label: "Function Audit Status" }, { key: "createdBy", label: "Function Created By" }, { key: "createdDate", label: "Function Created Date" }, { key: "updatedBy", label: "Function Updated By" }, { key: "updatedDate", label: "Function Updated Date" },];
  const renderTableHeaders = () => (
    <tr>
      <th><input type="checkbox" checked={selectAll} onChange={handleSelectAllChange} /></th>
      {columns.map(header => (
        <th key={header.key}>
          {header.sortable ? (
            <div className="sortable-header" onClick={() => requestSort(header.key)}>
              {header.label}<span className="sort-icon">{getSortIcon(header.key)}</span>
            </div>
          ) : header.label}
        </th>
      ))}
    </tr>
  );
  const handleNavigateToFunctionDetails = (func: any) => {
    navigate(`/functionCampaigndetailsinfo/${campaignId}/${func.functionId}`);
  };
  const renderTableRows = () => {
    if (!functions.length) return <tr><td colSpan={columns.length + 1} className="no-data">No Campaign Functions Details found</td></tr>;
    return functions.map(func => (
      <tr key={func.functionId}>
        <td><input type="checkbox" checked={selectedFunctions.has(func.functionId)} onChange={() => handleCheckboxChange(func.functionId)} /></td>
        <td className="clickable" onClick={() => handleNavigateToFunctionDetails(func)}>{getFunctionDescription(func.functionName)}</td>
        <td>{func.functionHeadName || "N/A"}</td>
        <td>{func.functionHeadEmail || "N/A"}</td>
        <td>{func.functionSpocName || "N/A"}</td>
        <td>{func.functionSpocEmail || "N/A"}</td>
        <td>{getCampaignStatusDescription(func.auditStatus || "N/A")}</td>
        <td>{func.createdBy || "N/A"}</td>
        <td>{formatDate(func.createdDate)}</td>
        <td>{func.updatedBy || "N/A"}</td>
        <td>{formatDate(func.updatedDate)}</td>
      </tr>
    ));
  };
  const handleAddFunction = () => {
    navigate(`/addcampaignfunction/${campaignName}`, { state: { campaignId } });
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Function Campaign Details - {campaignName}</h2>
        <div className="table-title-actions">
          {selectedFunctions.size > 0 && fields.delete && campaignStatus === "NOT_STARTED" && (
            <div className="remove-button-wrapper">
              <button className="addbtn" onClick={handleRemoveSelectedFunctions}>Remove functions</button>
            </div>
          )}
          {fields.create && campaignStatus === "NOT_STARTED" && (
            <button className="addbtn" onClick={handleAddFunction}>Add Function</button>
          )}
          <ExportButton data={functions} columns={columns} fetchAllData={fetchAllDataForExport} fileName={`functions-campaign-details-${campaignId}`} />
        </div>
      </div>
      <div className="table-container">
        <div className="table-inner-container">
          <table className="main-table">
            <thead>{renderTableHeaders()}</thead>
            <tbody>{renderTableRows()}</tbody>
          </table>
        </div>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} totalElements={totalElements} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} onItemsPerPageChange={setItemsPerPage} />
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default FunctionCampaignDetails;